import { IconCheckmark, Menu, MenuHeading, MenuItem, MenuPopper } from '@zillow/constellation';
import { useContext, useState } from 'react';
import { Folder, ReadRepliedStatus, RenterProgressStatus } from '../../types/GraphqlTypes';
import { emitOpenFilterMenuEvent, OPEN_FILTER_MENU_EVENT_TYPE_ID } from '../../constants/clickstreamEvents/filterMenu';
import { emitStarredConversationsFilterEvent } from '../../constants/clickstreamEvents/starConversations';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import stringUtils from '../../utils/stringUtils';
import Icon from './Icon';
import {
  StyledDivCaretIcon,
  StyledIconCheckmarkWrapper,
  StyledIconChevron,
  StyledTextButton,
} from './StyledComponents';
import { useAuth } from '@zg-rentals/auth-comet';

const FoldersModule = () => {
  const { folder } = useContext(InboxPageContext);
  const { changeFiltersOrFolders } = useInboxRouterHelper();
  const { user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { isViewArchive, isViewInbox, isViewSpam, isViewFavorites } = (() => {
    return {
      isViewInbox: folder === Folder.Inbox,
      isViewArchive: folder === Folder.Archived,
      isViewSpam: folder === Folder.Spam,
      isViewFavorites: folder === Folder.Favorite,
    };
  })();

  const getLabel = (folder: Folder): string => {
    if (folder === Folder.Archived) {
      // UI should show Archive instead of Archived (no 'd')
      // but the BE, Graphql server, everything, uses term "archived"
      return 'Archive';
    } else if (folder === Folder.Spam) {
      return 'Reported';
    } else if (folder === Folder.Favorite) {
      return 'Starred';
    }

    return stringUtils.capitalize(folder);
  };

  const handleToggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);

    if (!isMenuOpen) {
      emitOpenFilterMenuEvent(user);
    }
  };

  const getHandleViewClickFn = (folderOption: Folder) => {
    return () => {
      if (folderOption === Folder.Favorite) {
        emitStarredConversationsFilterEvent(user);
      }

      if (folderOption !== folder) {
        changeFiltersOrFolders({
          folder: folderOption,
          // Reset all other filters,
          renterProgressStatus: RenterProgressStatus.ShowAll,
          readRepliedStatus: ReadRepliedStatus.ShowAll,
          byProperty: false,
        });
      }
    };
  };

  return (
    <MenuPopper
      triggered={
        <Menu>
          <MenuHeading>View</MenuHeading>
          <MenuItem onClick={getHandleViewClickFn(Folder.Inbox)} icon={<Icon isInbox isGray />}>
            Inbox
            {isViewInbox && <Checkmark />}
          </MenuItem>

          <MenuItem onClick={getHandleViewClickFn(Folder.Favorite)} icon={<Icon isStar isGray />}>
            Starred
            {isViewFavorites && <Checkmark />}
          </MenuItem>

          <MenuItem onClick={getHandleViewClickFn(Folder.Archived)} icon={<Icon isArchived isGray />}>
            Archive
            {isViewArchive && <Checkmark />}
          </MenuItem>

          <MenuItem onClick={getHandleViewClickFn(Folder.Spam)} icon={<Icon isSpam isGray />}>
            Report
            {isViewSpam && <Checkmark />}
          </MenuItem>
        </Menu>
      }
      isOpen={isMenuOpen}
      onClose={() => setIsMenuOpen(false)}
    >
      <StyledTextButton
        data-eventid={OPEN_FILTER_MENU_EVENT_TYPE_ID}
        aria-label={`Show view by filters, currently viewing ${folder}`}
        buttonType="secondary"
        fontType="body"
        onClick={handleToggleMenu}
        icon={
          <Icon
            isArchived={folder === Folder.Archived}
            isSpam={folder === Folder.Spam}
            isInbox={folder === Folder.Inbox}
            isStar={folder === Folder.Favorite}
          />
        }
      >
        {getLabel(folder)}
        <StyledDivCaretIcon>
          <StyledIconChevron $isActive={isMenuOpen} fontColor="blue400" size="xs" />
        </StyledDivCaretIcon>
      </StyledTextButton>
    </MenuPopper>
  );
};

export default FoldersModule;

const Checkmark = () => {
  return (
    <StyledIconCheckmarkWrapper display="flex" alignItems="center">
      <IconCheckmark fontColor="brand" size="xs" />
    </StyledIconCheckmarkWrapper>
  );
};
